import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@kcftech/design-system';
import {
    FavoriteType,
    ILocationResponse,
    ISystemOverviewResponse,
} from '@kcftech/prometheus-client';
import { CreateButton } from 'components/button/CreateButton';
import { DocumentTitle } from 'components/DocumentTitle';
import { NoFavorites } from 'components/favorites/NoFavorites';
import { DeleteSystemModal } from 'components/modal/DeleteSystemModal';
import { SystemSettingsModal } from 'components/modal/SystemSettingsModal';
import { BasePage } from 'components/page/BasePage';
import { PagingBar } from 'components/paging/PagingBar';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { Tab, TabList, TabPanel, Tabs } from 'components/tabs/Tabs';
import { SystemTile } from 'components/tiles/SystemTile';
import { usePermissions } from 'contexts/PermissionsContext';
import { useUser } from 'contexts/UserContext';
import { useLocationSystemsPagedDataSource } from 'custom_hooks/data_hooks/data_source/locationSystemsDataSource';
import {
    invalidateSystemQuery,
    useSystem,
} from 'custom_hooks/data_hooks/system/useSystem';
import { AcknowledgeAlertsModal } from 'pages/workbench/AcknowledgeAlertsModal';
import { GenerateAlarmThresholdsModal } from 'pages/workbench/AlarmThresholds/GenerateAlarmThresholdsModal';
import { useEffect, useState } from 'react';
import { tokens } from 'styles/tokens';

export enum SystemTabs {
    AllSystems = 0,
    Favorites = 1,
}

/**
 * Number of systems per page
 */
const SystemsPageSize = 20;

export type SystemsOverviewPageProps = Readonly<{
    location: ILocationResponse;
}>;

export function SystemsOverviewPage({ location }: SystemsOverviewPageProps) {
    const { canCreateSystem, canGenerateAlarmThresholds } = usePermissions();
    const { favorites, updateFavorites } = useUser();
    const canAccessGenerateAlarmThresholds =
        location.LocationPermissions.IsAlertThresholdGenerationEnabled &&
        canGenerateAlarmThresholds;
    const [tab, setTab] = useState<SystemTabs>(0);
    const [selectedSystem, setSelectedSystem] = useState<ISystemOverviewResponse>();
    const [systemSettingsModalOpen, setSystemSettingsModalOpen] = useState(false);
    const [generateAlarmsModalOpen, setGenerateAlarmsModalOpen] = useState(false);
    const [acknowledgeAlertsModalOpen, setAcknowledgeAlertsModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    const { data: systemToEdit, isLoading: isLoadingSystems } = useSystem(
        selectedSystem?.Id,
    );

    const isFavoriteTab = tab === SystemTabs.Favorites;

    const {
        results: systemData,
        isLoading,
        paging,
        setPage,
        setFilter,
        reloadPage,
    } = useLocationSystemsPagedDataSource({
        name: 'location-system-overviews',
        defaultFilter: {
            locationId: location.Id,
            isFavorite: isFavoriteTab ? true : undefined,
        },
        pageLimit: SystemsPageSize,
    });

    useEffect(() => {
        setFilter((currentState) => ({
            ...currentState,
            locationId: location.Id,
            isFavorite: isFavoriteTab ? true : undefined,
        }));
    }, [location, isFavoriteTab, setFilter]);

    const onSystemSave = () => {
        reloadPage();
        if (selectedSystem) {
            invalidateSystemQuery(selectedSystem.Id);
        }
        closeSystemSettingsModal();
    };

    const closeSystemSettingsModal = () => {
        setSelectedSystem(undefined);
        setSystemSettingsModalOpen(false);
    };

    const onSystemSettingsClick = (system: ISystemOverviewResponse) => {
        setSystemSettingsModalOpen(true);
        setSelectedSystem(system);
    };

    const onGenerateAlarmThresholdsClick = (system: ISystemOverviewResponse) => {
        if (!canAccessGenerateAlarmThresholds) {
            return;
        }
        setSelectedSystem(system);
        setGenerateAlarmsModalOpen(true);
    };

    const onGenerateAlarmThresholdsForLocationClick = () => {
        if (!canAccessGenerateAlarmThresholds) {
            return;
        }
        setGenerateAlarmsModalOpen(true);
    };

    const onAcknowledgeAlertsClick = (system: ISystemOverviewResponse) => {
        setSelectedSystem(system);
        setAcknowledgeAlertsModalOpen(true);
    };

    const onSystemDeleteClick = (system: ISystemOverviewResponse) => {
        setSelectedSystem(system);
        setDeleteModalOpen(true);
    };

    const onGenerateAlarmsModalClose = () => {
        setSelectedSystem(undefined);
        setGenerateAlarmsModalOpen(false);
    };

    const onAcknowledgeModalClose = (acknowledged: boolean) => {
        setSelectedSystem(undefined);
        setAcknowledgeAlertsModalOpen(false);

        if (acknowledged) {
            reloadPage();
        }
    };

    const onDeleteModalClose = async (wasDeleted: boolean) => {
        setSelectedSystem(undefined);
        setDeleteModalOpen(false);

        if (wasDeleted) {
            await updateFavorites();
            reloadPage();
        }
    };

    const systems = systemData ?? [];
    const noFavorites =
        !favorites?.some((f) => f.FavoriteType.FavoriteTypeName.endsWith('System')) &&
        systems.length === 0 &&
        isFavoriteTab;

    return (
        <>
            <BasePage className="tile-page">
                <DocumentTitle title={`${location.Name} Workbench`} />
                <Tabs selectedIndex={tab} onSelect={(index) => setTab(index)}>
                    <div className="flex-row justify-between align-center">
                        <TabList className="flex align-center f-20">
                            <Tab>All Systems</Tab>
                            <Tab>
                                <FontAwesomeIcon
                                    className="pr-5 mb-2"
                                    color={tokens.misc.favorite}
                                    icon={faStar}
                                />
                                Favorites
                            </Tab>
                        </TabList>
                        <div>
                            {canAccessGenerateAlarmThresholds && (
                                <Button
                                    className="mr-10"
                                    appearance="secondary"
                                    onClick={onGenerateAlarmThresholdsForLocationClick}
                                >
                                    Generate Alarm Thresholds
                                </Button>
                            )}
                            <Button
                                className="mr-10"
                                appearance="secondary"
                                onClick={() => setAcknowledgeAlertsModalOpen(true)}
                            >
                                Acknowledge Alerts
                            </Button>
                            {canCreateSystem && (
                                <CreateButton
                                    onClick={() => setSystemSettingsModalOpen(true)}
                                />
                            )}
                        </div>
                        {systemSettingsModalOpen && !isLoadingSystems && (
                            <SystemSettingsModal
                                systemToEdit={systemToEdit}
                                locationId={location.Id}
                                onSave={onSystemSave}
                                onCancel={closeSystemSettingsModal}
                            />
                        )}
                    </div>
                    {/* All Systems Content */}
                    <TabPanel>
                        <SpinnerArea isLoading={isLoading} hideChildren={isLoading}>
                            <div className="pt-50 tile-page-grid">
                                {systems.map((system) => (
                                    <SystemTile
                                        key={system.Id}
                                        system={system}
                                        onEdit={() => onSystemSettingsClick(system)}
                                        onGenerateAlarmThresholds={
                                            canAccessGenerateAlarmThresholds
                                                ? () =>
                                                      onGenerateAlarmThresholdsClick(
                                                          system,
                                                      )
                                                : undefined
                                        }
                                        onAcknowledgeAlerts={() =>
                                            onAcknowledgeAlertsClick(system)
                                        }
                                        onDelete={() => onSystemDeleteClick(system)}
                                    />
                                ))}
                            </div>
                        </SpinnerArea>
                        {paging.totalResults !== undefined && paging.totalResults > 0 && (
                            <PagingBar
                                className="pt-20"
                                currentPage={paging.page}
                                onPageChange={setPage}
                                totalPages={paging.totalPages ?? 1}
                                total={paging.totalResults}
                                pageSize={SystemsPageSize}
                            />
                        )}
                    </TabPanel>
                    {/* Favorites Content */}
                    <TabPanel>
                        <SpinnerArea isLoading={isLoading} hideChildren={isLoading}>
                            {noFavorites ? (
                                <NoFavorites favoriteType={FavoriteType.System} />
                            ) : (
                                <div className="pt-50 tile-page-grid">
                                    {systems.map((system) => (
                                        <SystemTile
                                            key={system.Id}
                                            system={system}
                                            onEdit={() => onSystemSettingsClick(system)}
                                            onGenerateAlarmThresholds={
                                                canAccessGenerateAlarmThresholds
                                                    ? () =>
                                                          onGenerateAlarmThresholdsClick(
                                                              system,
                                                          )
                                                    : undefined
                                            }
                                            onAcknowledgeAlerts={() =>
                                                onAcknowledgeAlertsClick(system)
                                            }
                                            onDelete={() => onSystemDeleteClick(system)}
                                        />
                                    ))}
                                </div>
                            )}
                        </SpinnerArea>
                        {paging.totalResults !== undefined && paging.totalResults > 0 && (
                            <PagingBar
                                className="pt-20"
                                currentPage={paging.page}
                                onPageChange={setPage}
                                totalPages={paging.totalPages ?? 1}
                                total={paging.totalResults}
                                pageSize={SystemsPageSize}
                            />
                        )}
                    </TabPanel>
                </Tabs>
            </BasePage>
            {canAccessGenerateAlarmThresholds && generateAlarmsModalOpen && (
                <GenerateAlarmThresholdsModal
                    system={selectedSystem}
                    locationId={location.Id}
                    onClose={onGenerateAlarmsModalClose}
                />
            )}
            {acknowledgeAlertsModalOpen && (
                <AcknowledgeAlertsModal
                    system={selectedSystem}
                    location={location}
                    onClose={onAcknowledgeModalClose}
                />
            )}
            {deleteModalOpen && selectedSystem && (
                <DeleteSystemModal system={selectedSystem} onClose={onDeleteModalClose} />
            )}
        </>
    );
}
